import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../services/api';
import config from '../config/config';
import { Flag, CheckCircle, Eye, Calendar, FileText, AlertTriangle, MessageCircle } from 'lucide-react';

const Reports = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'all', 'pending', 'resolved'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [notification, setNotification] = useState(null);

  // Verificar que el usuario sea admin
  useEffect(() => {
    if (!config.isAdmin(currentUser)) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Cargar reportes
  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.page]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const resolved = filter === 'all' ? null : filter === 'resolved';
      const response = await apiService.getAllReports(resolved, pagination.page, pagination.limit);
      
      setReports(response.reports || []);
      setPagination({
        ...pagination,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      });
    } catch (error) {
      
      // Si el endpoint no existe (404), mostrar mensaje específico
      if (error.message.includes('no encontrado') || error.message.includes('404')) {
        setReports([]);
        // No mostrar notificación de error si el endpoint no existe aún
      } else {
        showNotification('Error al cargar reportes', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (reportId) => {
    try {
      await apiService.resolveReport(reportId);
      showNotification('Reporte marcado como resuelto', 'success');
      loadReports(); // Recargar lista
    } catch (error) {
      showNotification('Error al resolver reporte', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      fake_profile: 'Perfil falso',
      inappropriate_content: 'Contenido inapropiado',
      harassment: 'Acoso',
      spam: 'Spam',
      underage: 'Menor de edad',
      other: 'Otro'
    };
    return reasons[reason] || reason;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && reports.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Flag className="h-8 w-8 text-primary" />
            <span>Administración de Reportes</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Gestiona los reportes de usuarios
          </p>
        </div>

        {/* Notificación */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg ${
            notification.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {notification.message}
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Filtrar:</span>
            <button
              onClick={() => {
                setFilter('pending');
                setPagination({ ...pagination, page: 1 });
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'pending'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pendientes
            </button>
            <button
              onClick={() => {
                setFilter('resolved');
                setPagination({ ...pagination, page: 1 });
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'resolved'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Resueltos
            </button>
            <button
              onClick={() => {
                setFilter('all');
                setPagination({ ...pagination, page: 1 });
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            <span className="text-sm text-gray-600 ml-auto">
              Total: {pagination.total} reportes
            </span>
          </div>
        </div>

        {/* Lista de reportes */}
        {reports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Flag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No hay reportes para mostrar</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div key={report.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  {/* Información del reporte */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        report.is_resolved
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {report.is_resolved ? 'Resuelto' : 'Pendiente'}
                      </div>
                      <div className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        {getReasonLabel(report.reason)}
                      </div>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(report.created_at)}
                      </span>
                    </div>

                    {/* Descripción */}
                    {report.description && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700 flex items-start">
                          <FileText className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{report.description}</span>
                        </p>
                      </div>
                    )}

                    {/* Información de los usuarios */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Reportador */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-2">Reportado por:</p>
                        <div className="flex items-center space-x-3">
                          <img
                            src={report.reporter_profile_picture || '/images/default-avatar.svg'}
                            alt={report.reporter_username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {report.reporter_first_name || report.reporter_username}
                            </p>
                            <p className="text-sm text-gray-600">@{report.reporter_username}</p>
                          </div>
                        </div>
                      </div>

                      {/* Usuario reportado */}
                      <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                        <p className="text-xs text-red-700 mb-2 flex items-center">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Usuario reportado:
                        </p>
                        <div className="flex items-center space-x-3">
                          <img
                            src={report.reported_profile_picture || '/images/default-avatar.svg'}
                            alt={report.reported_username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {report.reported_first_name || report.reported_username}
                            </p>
                            <p className="text-sm text-gray-600">@{report.reported_username}</p>
                            {!report.reported_is_active && (
                              <span className="text-xs text-red-600">(Cuenta desactivada)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center space-x-3 flex-wrap gap-2">
                      <Link
                        to={`/perfil/${report.reported_user_id}`}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Ver perfil</span>
                      </Link>

                      <Link
                        to={`/admin/conversaciones/${report.reported_user_id}`}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <MessageCircle className="h-4 w-4" />
                        <span>Ver conversaciones</span>
                      </Link>
                      
                      {!report.is_resolved && (
                        <button
                          onClick={() => handleResolve(report.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Marcar resuelto</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginación */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex justify-center space-x-2">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            <span className="px-4 py-2 text-gray-700">
              Página {pagination.page} de {pagination.pages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;

